import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Repeat as RepeatIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Menu, Staff } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface Customer {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  phoneNumber: string;
  userLineId?: string;
}

interface CreateReservationDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateReservationDialog: React.FC<CreateReservationDialogProps> = ({
  open,
  customer,
  onClose,
  onSuccess,
}) => {
  const { shop } = useAuth();
  const [loading, setLoading] = useState(false);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 基本情報
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [reservationDate, setReservationDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [notes, setNotes] = useState('');

  // カスタムメッセージ
  const [customMessage, setCustomMessage] = useState('');
  const [useCustomMessage, setUseCustomMessage] = useState(false);

  // リマインダー設定
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderDaysBefore, setReminderDaysBefore] = useState(1);
  const [customReminderMessage, setCustomReminderMessage] = useState('');
  const [useCustomReminder, setUseCustomReminder] = useState(false);

  // 繰り返し予約
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringPattern, setRecurringPattern] = useState<'weekly' | 'biweekly' | 'monthly' | 'custom'>('weekly');
  const [customInterval, setCustomInterval] = useState(7);
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringCount, setRecurringCount] = useState(4);

  useEffect(() => {
    if (open && shop) {
      fetchMenus();
      fetchStaff();
    }
  }, [open, shop]);

  useEffect(() => {
    if (selectedMenuId) {
      const menu = menus.find(m => m.id === selectedMenuId);
      if (menu) {
        // メニューのデフォルトリマインダー日数を設定
        if (menu.defaultReminderDays) {
          setReminderDaysBefore(menu.defaultReminderDays);
        }
        // メニューのテンプレートメッセージを設定
        if (menu.messageTemplates?.beforeVisit) {
          setCustomMessage(menu.messageTemplates.beforeVisit);
          setUseCustomMessage(true);
        }
        if (menu.messageTemplates?.reminder) {
          setCustomReminderMessage(menu.messageTemplates.reminder);
        }
      }
    }
  }, [selectedMenuId, menus]);

  const fetchMenus = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'menus'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Menu[];
      setMenus(data);
    } catch (error) {
      console.error('メニュー取得エラー:', error);
    }
  };

  const fetchStaff = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'staff'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Staff[];
      setStaff(data);
    } catch (error) {
      console.error('スタッフ取得エラー:', error);
    }
  };

  const calculateEndTime = (start: string, duration: number): string => {
    const [hours, minutes] = start.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const generateRecurringDates = (baseDate: Date, pattern: string, interval: number, count: number): Date[] => {
    const dates: Date[] = [];
    for (let i = 0; i < count; i++) {
      const date = new Date(baseDate);

      switch (pattern) {
        case 'weekly':
          date.setDate(baseDate.getDate() + (i * 7));
          break;
        case 'biweekly':
          date.setDate(baseDate.getDate() + (i * 14));
          break;
        case 'monthly':
          date.setMonth(baseDate.getMonth() + i);
          break;
        case 'custom':
          date.setDate(baseDate.getDate() + (i * interval));
          break;
      }

      dates.push(date);
    }
    return dates;
  };

  const checkBlockedSlots = async (date: Date, start: string, end: string, staffId: string | null): Promise<boolean> => {
    if (!shop) return false;

    try {
      // ブロックされた枠を取得
      const blockedSlotsQuery = query(
        collection(db, 'blockedSlots'),
        where('shopId', '==', shop.id),
        where('blockedDate', '==', Timestamp.fromDate(date))
      );

      const blockedSnapshot = await getDocs(blockedSlotsQuery);

      for (const doc of blockedSnapshot.docs) {
        const blocked = doc.data();

        // スタッフ指定がない（全体ブロック）または同じスタッフの場合
        if (blocked.staffId === null || blocked.staffId === staffId) {
          // 時間が重複するかチェック
          if (start < blocked.endTime && end > blocked.startTime) {
            return true; // ブロックされている
          }
        }
      }

      return false;
    } catch (error) {
      console.error('ブロックチェックエラー:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!customer || !shop || !selectedMenuId || !reservationDate || !startTime) {
      setError('必須項目を入力してください');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const menu = menus.find(m => m.id === selectedMenuId);
      const selectedStaff = selectedStaffId ? staff.find(s => s.id === selectedStaffId) : null;

      if (!menu) {
        throw new Error('メニューが見つかりません');
      }

      const baseDate = new Date(reservationDate);
      const endTime = calculateEndTime(startTime, menu.duration);

      // 繰り返し予約の場合、複数の予約を作成
      const datesToCreate = isRecurring
        ? generateRecurringDates(baseDate, recurringPattern, customInterval, recurringCount)
        : [baseDate];

      // 繰り返し予約のグループIDを生成
      const parentId = isRecurring ? `recurring_${Date.now()}` : undefined;

      // 全ての日付でブロックチェック
      for (const resDate of datesToCreate) {
        const isBlocked = await checkBlockedSlots(resDate, startTime, endTime, selectedStaffId || null);
        if (isBlocked) {
          throw new Error(`${resDate.toLocaleDateString('ja-JP')} ${startTime}は予約枠がブロックされています`);
        }
      }

      for (let i = 0; i < datesToCreate.length; i++) {
        const resDate = datesToCreate[i];

        const reservationData: any = {
          shopId: shop.id,
          userId: customer.userId,
          userName: customer.displayName,
          userEmail: customer.email,
          userPhone: customer.phoneNumber,
          userLineId: customer.userLineId,
          menuId: menu.id,
          menuName: menu.name,
          staffId: selectedStaffId || null,
          staffName: selectedStaff?.name || null,
          reservationDate: Timestamp.fromDate(resDate),
          startTime: startTime,
          endTime: endTime,
          duration: menu.duration,
          totalPrice: menu.price + (selectedStaff?.nominationFee || 0),
          status: 'confirmed', // 店舗側からの予約は自動承認
          notes: notes || undefined,
          createdBy: 'shop',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        // カスタムメッセージ
        if (useCustomMessage && customMessage) {
          reservationData.customMessage = customMessage;
        }

        // リマインダー設定
        if (reminderEnabled) {
          reservationData.reminderSettings = {
            enabled: true,
            daysBefore: reminderDaysBefore,
            customMessage: useCustomReminder ? customReminderMessage : undefined,
          };
        }

        // 繰り返し予約情報
        if (isRecurring) {
          reservationData.recurringReservation = {
            parentId: parentId,
            pattern: recurringPattern,
            interval: recurringPattern === 'custom' ? customInterval : undefined,
            endDate: recurringEndDate ? Timestamp.fromDate(new Date(recurringEndDate)) : undefined,
            occurrenceCount: i + 1,
          };
        }

        await addDoc(collection(db, 'reservations'), reservationData);
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('予約作成エラー:', error);
      setError('予約の作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // リセット
    setSelectedMenuId('');
    setSelectedStaffId('');
    setReservationDate('');
    setStartTime('');
    setNotes('');
    setCustomMessage('');
    setUseCustomMessage(false);
    setReminderEnabled(true);
    setReminderDaysBefore(1);
    setCustomReminderMessage('');
    setUseCustomReminder(false);
    setIsRecurring(false);
    setRecurringPattern('weekly');
    setCustomInterval(7);
    setRecurringEndDate('');
    setRecurringCount(4);
    setError(null);
    onClose();
  };

  const selectedMenu = menus.find(m => m.id === selectedMenuId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        予約作成 - {customer?.displayName}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {customer && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">顧客情報</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>名前:</strong> {customer.displayName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>電話:</strong> {customer.phoneNumber}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    <strong>LINE:</strong>
                  </Typography>
                  <Chip
                    label={customer.userLineId ? '連携済み' : '未連携'}
                    size="small"
                    color={customer.userLineId ? 'success' : 'default'}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}

        <Grid container spacing={2}>
          {/* 基本情報 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>基本情報</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>メニュー</InputLabel>
              <Select
                value={selectedMenuId}
                label="メニュー"
                onChange={(e) => setSelectedMenuId(e.target.value)}
              >
                {menus.map(menu => (
                  <MenuItem key={menu.id} value={menu.id}>
                    {menu.name} (¥{menu.price.toLocaleString()} / {menu.duration}分)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>担当スタッフ</InputLabel>
              <Select
                value={selectedStaffId}
                label="担当スタッフ"
                onChange={(e) => setSelectedStaffId(e.target.value)}
              >
                <MenuItem value="">指定なし</MenuItem>
                {staff
                  .filter(s => {
                    if (!selectedMenuId) return true;
                    const menu = menus.find(m => m.id === selectedMenuId);
                    return !menu?.staffIds || menu.staffIds.length === 0 || menu.staffIds.includes(s.id);
                  })
                  .map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} {s.nominationFee ? `(+¥${s.nominationFee})` : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="date"
              label="予約日"
              value={reservationDate}
              onChange={(e) => setReservationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="time"
              label="開始時刻"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {selectedMenu && startTime && (
            <Grid item xs={12}>
              <Alert severity="info">
                終了予定時刻: {calculateEndTime(startTime, selectedMenu.duration)}
                (所要時間: {selectedMenu.duration}分)
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="備考"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="予約に関する備考を入力してください"
            />
          </Grid>

          {/* カスタムメッセージ */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <MessageIcon sx={{ mr: 1 }} />
                <Typography>カスタムメッセージ（予約確認時）</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useCustomMessage}
                        onChange={(e) => setUseCustomMessage(e.target.checked)}
                      />
                    }
                    label="カスタムメッセージを使用"
                  />
                  {useCustomMessage && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="予約確認メッセージ"
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="例: 〇〇週目の検診です。当日は△△の準備をお願いします。"
                      sx={{ mt: 2 }}
                      helperText="予約確認時にこのメッセージがLINEで送信されます"
                    />
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* リマインダー設定 */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <NotificationsIcon sx={{ mr: 1 }} />
                <Typography>リマインダー設定</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reminderEnabled}
                        onChange={(e) => setReminderEnabled(e.target.checked)}
                      />
                    }
                    label="リマインダーを有効にする"
                  />
                  {reminderEnabled && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        type="number"
                        label="何日前に通知"
                        value={reminderDaysBefore}
                        onChange={(e) => setReminderDaysBefore(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 0, max: 30 }}
                        sx={{ mb: 2 }}
                        helperText="0を指定すると当日の朝9時に通知"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={useCustomReminder}
                            onChange={(e) => setUseCustomReminder(e.target.checked)}
                          />
                        }
                        label="カスタムリマインダーメッセージ"
                      />
                      {useCustomReminder && (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="リマインダーメッセージ"
                          value={customReminderMessage}
                          onChange={(e) => setCustomReminderMessage(e.target.value)}
                          placeholder="例: 明日の検診の持ち物: 母子手帳、保険証"
                          sx={{ mt: 2 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* 繰り返し予約 */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <RepeatIcon sx={{ mr: 1 }} />
                <Typography>繰り返し予約</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isRecurring}
                        onChange={(e) => setIsRecurring(e.target.checked)}
                      />
                    }
                    label="定期予約として作成"
                  />
                  {isRecurring && (
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>繰り返しパターン</InputLabel>
                        <Select
                          value={recurringPattern}
                          label="繰り返しパターン"
                          onChange={(e) => setRecurringPattern(e.target.value as any)}
                        >
                          <MenuItem value="weekly">毎週</MenuItem>
                          <MenuItem value="biweekly">隔週</MenuItem>
                          <MenuItem value="monthly">毎月</MenuItem>
                          <MenuItem value="custom">カスタム</MenuItem>
                        </Select>
                      </FormControl>

                      {recurringPattern === 'custom' && (
                        <TextField
                          fullWidth
                          type="number"
                          label="間隔（日数）"
                          value={customInterval}
                          onChange={(e) => setCustomInterval(parseInt(e.target.value) || 7)}
                          inputProps={{ min: 1, max: 365 }}
                          sx={{ mb: 2 }}
                        />
                      )}

                      <TextField
                        fullWidth
                        type="number"
                        label="作成する予約の回数"
                        value={recurringCount}
                        onChange={(e) => setRecurringCount(parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 52 }}
                        helperText={`${recurringCount}回分の予約を一度に作成します`}
                      />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedMenuId || !reservationDate || !startTime}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? '作成中...' : isRecurring ? `${recurringCount}件の予約を作成` : '予約を作成'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateReservationDialog;
